import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card';
import { CONFIG } from 'variables/config';
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDelete, MdEdit, MdLock, MdPause, MdPlayArrow, MdWaterfallChart } from 'react-icons/md';
import WaterfallOfferForm, { AnswerField, CatalogIndustry, WaterfallOfferDraft } from './WaterfallOfferForm';
import WaterfallLeadsSection from './WaterfallLeadsSection';

// Page Waterfall : offres « locked » qui reçoivent, dans l'ordre, les leads refusés par Batiweb ou
// jamais envoyés à Batiweb.
// Back : controller/waterfallController.ts, services/integration/batiwebWaterfall.ts.

interface WaterfallOffer {
    _id: string;
    name: string | null;
    // Code de l'offre vu par l'acheteur (« #… » dans ses commandes et ses leads).
    offerBuyerNumber: string | null;
    buyer: { _id: string; email: string; name?: string; company?: string } | null;
    industry: string | null;
    subIndustry: string | null;
    regions: string[];
    buyerPrice: number;
    waterfallPriority: number | null;
    waterfallPaused: boolean;
    refusedAnswers: Record<string, string[]>;
    stats: { total: number; last30Days: number; lastDispatchedAt: string | null };
}

// Nouvelle offre : mêmes refus que la plateforme (locataire, appartement…).
const emptyDraft = (answerFields: AnswerField[]): WaterfallOfferDraft => ({
    name: '',
    buyerId: '',
    industry: '',
    subIndustries: [],
    regions: [],
    buyerPrice: '0',
    refusedAnswers: Object.fromEntries(answerFields.map((f) => [f.id, f.globallyBlocked])),
});

// Écarts de l'offre avec la plateforme : réponses bloquées qu'elle accepte, autres qu'elle refuse.
const profileDiff = (offer: { refusedAnswers: Record<string, string[]> }, answerFields: AnswerField[]) => {
    const accepts: string[] = [];
    const refuses: string[] = [];
    for (const f of answerFields) {
        const refused = offer.refusedAnswers[f.id] || [];
        accepts.push(...f.globallyBlocked.filter((a) => f.answers.includes(a) && !refused.includes(a)));
        refuses.push(...refused.filter((a) => !f.globallyBlocked.includes(a)));
    }
    return { accepts, refuses };
};

// Schéma du parcours : après le no match, le lead passe par Batiweb puis par les intégrations
// diverses, ou saute chacune de ces étapes s'il n'y est jamais envoyé ; les branches se rejoignent
// à l'étape suivante, jusqu'à l'offre fallback.
// `to` rend la case cliquable : la page cible affiche un bouton retour vers le waterfall.
const FlowStep = ({ n, title, detail, highlight, to }: { n?: number; title: string; detail: string; highlight?: boolean; to?: string }) => {
    const navigate = useNavigate();
    const content = (
        <>
            <div className="font-bold">
                {n ? `${n}. ` : ''}
                {title}
            </div>
            <div className="opacity-75">{detail}</div>
        </>
    );
    const color = highlight ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' : 'bg-gray-100 text-navy-700 dark:bg-navy-700 dark:text-white';
    if (!to) return <div className={`rounded-lg px-3 py-2 ${color}`}>{content}</div>;
    return (
        <button
            onClick={() => navigate(to, { state: { from: 'waterfall' } })}
            className={`rounded-lg px-3 py-2 text-left ring-brand-500 hover:ring-2 ${color}`}
        >
            {content}
        </button>
    );
};

const Arrow = () => <span className="text-gray-400">→</span>;

// Étape facultative : le lead y passe, ou la saute (case en pointillé) ; les deux branches se
// rejoignent (accolade) avant l'étape suivante.
const FlowFork = ({ n, title, detail, skipTitle, skipDetail, to }: { n: number; title: string; detail: string; skipTitle: string; skipDetail: string; to?: string }) => (
    <div className="flex items-stretch gap-2">
        <div className="flex flex-col gap-1.5">
            <FlowStep n={n} title={title} detail={detail} to={to} />
            <div className="rounded-lg border border-dashed border-gray-300 px-3 py-2 text-gray-500 dark:border-navy-600 dark:text-gray-400">
                <div className="font-bold">{skipTitle}</div>
                <div className="opacity-75">{skipDetail}</div>
            </div>
        </div>
        <span className="my-5 w-2 rounded-r-md border-y border-r border-gray-300 dark:border-navy-600" />
        <span className="self-center">
            <Arrow />
        </span>
    </div>
);

const FlowDiagram = () => (
    <div className="mb-6 flex flex-wrap items-center gap-2 text-xs">
        <FlowStep n={1} title="Plateforme" detail="Offres running (routage normal)" />
        <Arrow />
        <FlowStep n={2} title="No match" detail="Lead en vente sur la Bourse" />
        <Arrow />
        <FlowFork n={3} title="Batiweb" detail="Refus ou sans réponse sous 5 jours ouvrés" skipTitle="Sans Batiweb" skipDetail="Jamais envoyé (produit non couvert…)" to="/back-office/batiweb" />
        <FlowFork n={4} title="Intégrations diverses" detail="Toute autre intégration" skipTitle="Sans intégration diverse" skipDetail="Jamais envoyé" />
        <FlowStep n={5} title="Offre fallback" detail="Offres locked ci-dessous, dans l’ordre" highlight />
        <Arrow />
        <FlowStep n={6} title="Bourse du Lead" detail="Si aucune offre ne correspond" />
    </div>
);

const formatEuro = (value: number) => `${value.toFixed(2).replace('.', ',')} €`;
const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');

export default function BackOfficeWaterfall() {
    const [offers, setOffers] = useState<WaterfallOffer[]>([]);
    const [industries, setIndustries] = useState<CatalogIndustry[]>([]);
    const [answerFields, setAnswerFields] = useState<AnswerField[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState<WaterfallOfferDraft | null>(null);
    const [lockId, setLockId] = useState('');
    const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);

    const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    const fetchOffers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/waterfall/offers`, { headers: headers() });
            setOffers(res.data.offers);
            setIndustries(res.data.industries);
            setAnswerFields(res.data.answerFields || []);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger les offres du waterfall.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOffers();
    }, [fetchOffers]);

    const afterChange = (message: string) => {
        setSuccess(message);
        setError('');
        fetchOffers();
        // L'aperçu dépend des offres actives et de leur ordre.
        setLeadsRefreshKey((k) => k + 1);
    };

    const run = async (id: string, action: () => Promise<unknown>, message: string) => {
        try {
            setBusyId(id);
            setError('');
            await action();
            afterChange(message);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur.');
        } finally {
            setBusyId(null);
        }
    };

    const move = (index: number, delta: number) => {
        const ids = offers.map((o) => o._id);
        const [moved] = ids.splice(index, 1);
        ids.splice(index + delta, 0, moved);
        run(moved, () => axios.put(`${CONFIG.apiUrl}/admin/waterfall/order`, { offerIds: ids }, { headers: headers() }), 'Ordre du waterfall mis à jour.');
    };

    const togglePause = (offer: WaterfallOffer) =>
        run(
            offer._id,
            () => axios.put(`${CONFIG.apiUrl}/admin/waterfall/offers/${offer._id}`, { waterfallPaused: !offer.waterfallPaused }, { headers: headers() }),
            offer.waterfallPaused ? 'Offre réactivée dans le waterfall.' : 'Offre en pause : elle garde sa place mais ne reçoit plus de leads.'
        );

    const remove = (offer: WaterfallOffer) => {
        if (!window.confirm(`Retirer « ${offer.name || offer._id} » du waterfall ? L'offre passe en « closed » ; les leads déjà reçus restent à l'acheteur.`)) return;
        run(offer._id, () => axios.delete(`${CONFIG.apiUrl}/admin/waterfall/offers/${offer._id}`, { headers: headers() }), 'Offre retirée du waterfall.');
    };

    const lockExisting = () => {
        const id = lockId.trim();
        if (!id) return;
        if (!window.confirm("Passer cette offre en « locked » ? Elle ne recevra plus aucun lead du routage normal, seulement ceux du waterfall.")) return;
        run(id, () => axios.post(`${CONFIG.apiUrl}/admin/waterfall/offers/${id}/lock`, {}, { headers: headers() }), 'Offre existante ajoutée au waterfall.').then(() =>
            setLockId('')
        );
    };

    const editDraft = (offer: WaterfallOffer): WaterfallOfferDraft => ({
        _id: offer._id,
        name: offer.name || '',
        buyerId: offer.buyer?._id || '',
        buyerLabel: offer.buyer?.email,
        industry: offer.industry || '',
        subIndustries: offer.subIndustry ? [offer.subIndustry] : [],
        regions: offer.regions,
        buyerPrice: String(offer.buyerPrice),
        refusedAnswers: offer.refusedAnswers || {},
    });

    return (
        <div className="pt-[50px] flex flex-col gap-6">
            <Card extra="p-5">
                <div className="mb-2 flex items-center gap-3">
                    <MdWaterfallChart className="text-2xl text-brand-500" />
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Waterfall des acheteurs</h2>
                </div>
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                    Une offre <b>locked</b> ne reçoit aucun lead du routage normal ni de la Bourse. Elle reçoit seulement les leads refusés par Batiweb
                    (Annulé, sans réponse sous 5 jours ouvrés, ou non vendable) ou qui n'y ont jamais été envoyés, s'ils correspondent à sa typologie et
                    à ses profils acceptés, offre par offre dans l'ordre ci-dessous.
                </p>

                <FlowDiagram />

                {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
                {success && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{success}</div>}

                {form ? (
                    <WaterfallOfferForm
                        key={form._id || 'new'}
                        initial={form}
                        industries={industries}
                        answerFields={answerFields}
                        onCancel={() => setForm(null)}
                        onSaved={(message) => {
                            setForm(null);
                            afterChange(message);
                        }}
                    />
                ) : (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setForm(emptyDraft(answerFields))}
                            className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                        >
                            <MdAdd />
                            Nouvelle offre locked
                        </button>
                        <div className="ml-auto flex items-center gap-2">
                            <input
                                value={lockId}
                                onChange={(e) => setLockId(e.target.value)}
                                placeholder="ID d'une offre acheteur existante"
                                className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                            <button
                                onClick={lockExisting}
                                disabled={!lockId.trim() || !!busyId}
                                className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                            >
                                <MdLock />
                                Verrouiller
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="py-10 text-center text-gray-400">Chargement...</div>
                ) : offers.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">
                        Aucune offre locked : les leads refusés par Batiweb restent sur la Bourse du Lead.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Ordre</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Offre / acheteur</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Typologie</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Départements</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Prix / lead</th>
                                    <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Leads reçus</th>
                                    <th className="pb-3 text-xs font-bold uppercase text-gray-500"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {offers.map((offer, index) => (
                                    <tr key={offer._id} className={`border-b border-gray-100 align-top dark:border-navy-700 ${offer.waterfallPaused ? 'opacity-60' : ''}`}>
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-1">
                                                <span className="w-5 font-bold text-navy-700 dark:text-white">{index + 1}</span>
                                                <button
                                                    onClick={() => move(index, -1)}
                                                    disabled={index === 0 || !!busyId}
                                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-navy-700"
                                                    aria-label="Monter"
                                                >
                                                    <MdArrowUpward />
                                                </button>
                                                <button
                                                    onClick={() => move(index, 1)}
                                                    disabled={index === offers.length - 1 || !!busyId}
                                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-navy-700"
                                                    aria-label="Descendre"
                                                >
                                                    <MdArrowDownward />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="py-3 pr-4">
                                            <a href={`/back-office/offers/marketplace/${offer._id}`} className="font-semibold text-navy-700 hover:text-brand-500 dark:text-white">
                                                {offer.name || offer._id}
                                            </a>
                                            {offer.offerBuyerNumber && (
                                                <span
                                                    className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                                                    title="Code acheteur de l'offre"
                                                >
                                                    #{offer.offerBuyerNumber}
                                                </span>
                                            )}
                                            {offer.waterfallPaused && (
                                                <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">En pause</span>
                                            )}
                                            <div className="text-xs text-gray-500">{offer.buyer?.email || 'Acheteur introuvable'}</div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                                            {offer.subIndustry || 'Tous les produits'}
                                            <div className="text-xs text-gray-400">{offer.industry || '—'}</div>
                                            {(() => {
                                                const { accepts, refuses } = profileDiff(offer, answerFields);
                                                return (
                                                    <>
                                                        {accepts.length > 0 && <div className="text-xs text-green-600">Accepte : {accepts.join(', ')}</div>}
                                                        {refuses.length > 0 && <div className="text-xs text-red-500">Refuse : {refuses.join(', ')}</div>}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        <td className="max-w-[220px] py-3 pr-4 text-xs text-gray-600 dark:text-gray-300">
                                            {offer.regions.length ? offer.regions.join(', ') : 'Toute la France'}
                                        </td>
                                        <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">
                                            <div className={offer.buyerPrice === 0 ? 'font-semibold text-purple-700' : ''}>A : {formatEuro(offer.buyerPrice)}</div>
                                            <div className="font-sans text-gray-400">V : prix minimum</div>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            <div className="font-semibold">{offer.stats.total} au total</div>
                                            <div className="text-gray-400">{offer.stats.last30Days} sur 30 j · dernier {formatDate(offer.stats.lastDispatchedAt)}</div>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => setForm(editDraft(offer))}
                                                    disabled={!!busyId}
                                                    className="rounded-lg bg-gray-100 p-2 text-navy-700 hover:bg-gray-200 disabled:opacity-40 dark:bg-navy-700 dark:text-white"
                                                    title="Modifier"
                                                >
                                                    <MdEdit />
                                                </button>
                                                <button
                                                    onClick={() => togglePause(offer)}
                                                    disabled={!!busyId}
                                                    className="rounded-lg bg-gray-100 p-2 text-navy-700 hover:bg-gray-200 disabled:opacity-40 dark:bg-navy-700 dark:text-white"
                                                    title={offer.waterfallPaused ? 'Réactiver' : 'Mettre en pause'}
                                                >
                                                    {offer.waterfallPaused ? <MdPlayArrow /> : <MdPause />}
                                                </button>
                                                <button
                                                    onClick={() => remove(offer)}
                                                    disabled={!!busyId}
                                                    className="rounded-lg bg-gray-100 p-2 text-red-600 hover:bg-red-50 disabled:opacity-40 dark:bg-navy-700"
                                                    title="Retirer du waterfall"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card extra="p-5">
                <WaterfallLeadsSection refreshKey={leadsRefreshKey} onRun={fetchOffers} />
            </Card>
        </div>
    );
}
